<template>
<div>
  <!--    Loader-->
  <loading-panel :loading="loading"></loading-panel>

  <div class="row">
    <div class="col">
      <card>
        <div class="row justify-content-between mx-0">
          <h4 class="card-title">Event logs</h4>
        </div>
        <filter-bar
          :filters="filterSchema"
          :initial-value="filterValues"
          :result-count="totalRows"
          :per-page="perPage"
          :per-page-options="perPageOptions"
          saved-filters-key="event-logs"
          @change="onFiltersChanged"
          @update:perPage="onPerPageChange"
        ></filter-bar>

        <div v-if="events.length <= 0" class="mt-3" >
          <div class="font-icon-detail">
            <span style="font-size: 3em">
              <i class="fas fa-exclamation-triangle"></i>
            </span>
            <h4 class="mt-3">No events recorded.</h4>
          </div>
        </div>

        <div class="d-none d-sm-block">
          <div class="row" >
            <div class="col">
              <b-table v-if="events.length > 0" striped hover :items="events"
                        :fields="['user', 'date', 'feature', 'event']">
                <template v-slot:cell(user)="row">
                  <div>{{row.item.first_name}} {{row.item.last_name}}</div>
                  <small><i class="tim-icons icon-email-85"></i> {{row.item.email}}</small>
                </template>
                <template v-slot:cell(date)="row">
                  <div>{{getLocalDate(row.item.timestamp)}}, {{getLocalTime(row.item.timestamp)}}</div>
                </template>
                <template v-slot:cell(event)="row">
                  <div>{{row.item.description}}
                    <a :href="makeLink(row.item.additional_information, row.item.feature)">
                      {{row.item.additional_information}}
                    </a>
                  </div>
                </template>
              </b-table>
            </div>
          </div>
        </div>

        <div class="d-block d-sm-none">
          <div class="row" >
            <div class="col">
              <b-table v-if="events.length > 0" striped hover :items="events"
                       :fields="['event']">
                <template #head(event)="row">
                  <span>Event count: {{totalRows}}</span>
                </template>
                <template v-slot:cell(event)="row">
                  <div>{{row.item.description}}
                    <a :href="makeLink(row.item.additional_information, row.item.feature)">
                      {{row.item.additional_information}}
                    </a>
                  </div>
                  <div>
                    <small>Performed by: {{ performedBy(row.item) }}</small>
                  </div>
                  <div>
                    <small>Timestamp: {{getLocalDate(row.item.timestamp)}}, {{getLocalTime(row.item.timestamp)}}</small>
                  </div>
                </template>
              </b-table>
            </div>
          </div>
        </div>
        <b-pagination
          v-model="currentPage"
          :total-rows="totalRows"
          :per-page="perPage"
          v-on:input="getEvents(perPage, getOffset())"
        ></b-pagination>
      </card>
    </div>
  </div>
</div>
</template>

<script>
import {BTable, BPagination} from "bootstrap-vue";
import {API_LOCATION} from "@/api-config";
import axios from "axios";
import swal from "sweetalert2";
import {Auth} from "aws-amplify";
import {formatGroupDate, formatGroupTime} from "@/plugins/dates";

export default {
  name: "EventLogs",
  components: {
    BTable,
    BPagination
  },
  data() {
    return {
      loading: false,
      time_format: JSON.parse(localStorage.getItem('group')).time_format,
      date_format: JSON.parse(localStorage.getItem('group')).date_format,
      perPageOptions: [5, 10, 25, 50],
      events: [],
      features: [],
      currentPage: 1,
      perPage: 10,
      totalRows: 0,
      // FilterBar drives the first fetch via its initial @change; this guards it.
      didInitialFetch: false,
      // Flat filter values for the shared <filter-bar>. Seeded from the URL in mounted;
      // saved-views precedence is resolved by FilterBar. feature = feature id.
      filterValues: {
        user: '',
        feature: '',
        event: '',
        date: { mode: 'range', days: '', from: '', to: '' }
      }
    }
  },
  computed: {
    // Schema for the shared <filter-bar>: free-text user/event + single-select feature
    // (options come from the feature list returned by the logs endpoint) + a date range.
    filterSchema() {
      return [
        { key: 'user', type: 'text', label: 'User', placeholder: 'User', operatorLabel: 'contains', primary: true },
        { key: 'feature', type: 'select', label: 'Feature', primary: true,
          options: (Array.isArray(this.features) ? this.features : []).map(f => ({ label: f.name, value: f.id })) },
        { key: 'event', type: 'text', label: 'Event', placeholder: 'Event', operatorLabel: 'contains', primary: true },
        { key: 'date', type: 'daterange', label: 'Date', primary: true }
      ]
    }
  },
  methods: {
    performedBy(item) {
      const name = `${item.first_name || ''} ${item.last_name || ''}`.trim()
      return name || item.email
    },
    // FilterBar change: apply new values and (re)fetch. The first change is
    // FilterBar's initial saved-views resolution and drives the first load.
    onFiltersChanged(values){
      this.filterValues = {
        user: values.user || '',
        feature: (values.feature === '' || values.feature == null) ? '' : values.feature,
        event: values.event || '',
        date: { ...(values.date || { mode: 'range', days: '', from: '', to: '' }) }
      }
      if(!this.didInitialFetch){
        this.didInitialFetch = true
        this.getEvents(this.perPage, this.getOffset())
      } else {
        // Applying a filter/view lands on page 1; reset BEFORE computing the offset
        // so a stale page can't build an out-of-range offset.
        this.currentPage = 1
        this.getEvents(this.perPage, this.getOffset(), 'filter')
      }
    },
    onPerPageChange(n){
      this.perPage = n
      this.currentPage = 1
      this.getEvents(this.perPage, this.getOffset(), 'filter')
    },
    makeLink(string, feature){
      let id = ''
      if(feature === 'Bookings'){
        id = string.replace(/^.*#\s*(\d+).*$/, '$1');
        return '/bookings?selected_booking=' + id
      }
      if(feature === 'Purchases'){
        if(string.includes('#')){
          id = string.replace(/^.*#\s*(\d+).*$/, '$1');
          return '/purchases/transactions/details?order_number=' + id
        }
        return '/purchases/transactions?count=10&page=1'
      }
      if(feature === 'Game management'){
        id = string.replace(/^.*#\s*(\d+).*$/, '$1');
        if(id === 'copy'){
          return
        }
        return '/games/settings'
      }
      if(feature === 'Checklists'){
        id = string.replace(/^.*#\s*(\d+).*$/, '$1');
        if(id === 'copy'){
          return
        }
        return '/checklists/new'
      }
      if(feature === 'User management'){
        return '/settings/user-management'
      }
    },
    getLocalTime(d){
      return formatGroupTime(d)
    },
    getLocalDate(d){
      return formatGroupDate(d)
    },
    getAuthToken() {
      return new Promise ( function(resolve) {
        Auth.currentSession()
          .then(data =>{
            resolve(data.idToken.jwtToken)
          })
          .catch(err => console.log(err))
      })
    },
    // Derive from/to (yyyy-MM-dd, the format the logs endpoint expects) from the
    // FilterBar daterange value. Supports 'days' (relative window) and 'range' modes.
    computeDateRange(){
      const d = this.filterValues.date || {}
      let from = ''
      let to = ''
      if (d.mode === 'days' && d.days !== '' && d.days != null) {
        const today = new Date()
        const dt = new Date()
        dt.setDate(dt.getDate() - Number(d.days))
        from = dt.getFullYear() + '-' + ('0' + (dt.getMonth() + 1)).slice(-2) + '-' + ('0' + dt.getDate()).slice(-2)
        to = today.getFullYear() + '-' + ('0' + (today.getMonth() + 1)).slice(-2) + '-' + ('0' + today.getDate()).slice(-2)
      } else if (d.mode === 'range' && d.from && d.to) {
        from = d.from
        to = d.to
      }
      return { from, to }
    },
    async getEvents(count, offset, source) {
      if(source === 'filter'){
        this.currentPage = 1
      }
      const f = this.filterValues
      const feature = (f.feature === '' || f.feature == null) ? '' : f.feature
      const { from, to } = this.computeDateRange()

      this.loading = true
      let token = await this.getAuthToken()
      let config = { headers: { Authorization: token } }
      let companyId = localStorage.getItem('companyId')
      let url = API_LOCATION + 'companies/' + companyId + '/logs?count=' + count + '&offset=' + offset +
        "&user=" + encodeURIComponent(f.user) +
        "&event=" + encodeURIComponent(f.event)
        +"&feature=" + encodeURIComponent(feature)
        +"&from=" + encodeURIComponent(from)
        +"&to=" + encodeURIComponent(to)
       + '&tz=' + encodeURIComponent(Intl.DateTimeFormat().resolvedOptions().timeZone);


      axios.get(url, config)
        .then(response => {
          this.loading = false
          this.events = response.data.events
          this.features = response.data.features
          this.totalRows = response.data.total_rows
          this.addQueryParameters()
        })
        .catch(err => {
          this.loading = false
          if(err.response.status == 401){
            this.$router.push('/forbidden')
          } else {
            swal('Error!', 'Something went wrong', 'error')
          }
        })
    },
    getOffset(){
      return Number((this.currentPage * this.perPage) - this.perPage)
    },
    addQueryParameters() {
      this.$nextTick(() => {
        this.currentPage = this.$route.query.page
      });

      const d = this.filterValues.date || {}
      const newQuery = {
        ...this.$route.query,
        count: this.perPage,
        page: this.currentPage,
        user: this.filterValues.user,
        event: this.filterValues.event,
        feature: (this.filterValues.feature === '' || this.filterValues.feature == null) ? '' : this.filterValues.feature,
        to: d.to || '',
        from: d.from || '',
      }
      this.$router.replace({query: newQuery}).catch(err => {
        if (err.name !== 'NavigationDuplicated') {
          throw err;
        }
      });
    },
  },
  mounted() {
    // Default when the query omits count/page (e.g. arriving from a saved view with
    // only ?view=), otherwise Number(undefined) => NaN builds a bad fetch URL.
    this.perPage = Number(this.$route.query.count) || 10
    this.currentPage = Number(this.$route.query.page) || 1
    this.loading = true
    // Seed FilterBar's flat values from the URL (feature = id). FilterBar resolves
    // saved-views precedence (?view= > URL > default > last-used) and drives the
    // first fetch via its initial `change` (onFiltersChanged).
    const q = this.$route.query || {}
    this.filterValues = {
      user: q.user || '',
      feature: q.feature ? Number(q.feature) : '',
      event: q.event || '',
      date: { mode: 'range', days: '', from: q.from || '', to: q.to || '' }
    }
  }
}
</script>

<style scoped>

</style>
